import React, { useEffect, useRef, useState } from 'react';
import theme from './OptionConfiguratorTabs.module.scss';
import { classnames, Sticky } from '@ygtechnologies/bygr-frontend-component-library';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import { Button, Card, Dialog, useBreakpoint, Dot, SimpleTooltip } from '@ygtechnologies/rubus-ui';
import { Trans } from '@lingui/react/macro';
import { faFileSignature, faLoveseat, faStar, faSwatchbook, faLineColumns, faTag, faArrowLeft, faRightLeft, faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useOptionConfigurator } from '../OptionConfiguratorContext';
import { useIsBuyer } from '@/utils/hooks';
import { OptionPresetGroupType } from '@/services/option-preset/option-preset-group';
import { ConfiguratorRoomDto, ConfiguratorPresetGroupDto } from '@/services/configurator';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { buildConfiguratorUrl } from '../utils/buildConfiguratorUrl';
import { BuyerCurrencyDisplay } from '@/buyer/components/BuyerCurrencyDisplay';
import { ImmersiveSignAndSend } from '../Room/components/ImmersiveSignAndSend';
import { useShowroomActions } from '../useShowroomActions';
import { useIsCompactChrome, useCompleteInTopBar } from '../useConfiguratorLayout';
import { useConfiguratorPrimaryActions, PrimaryCompleteControl } from '../configuratorPrimaryActions';

const getUnselectedGroupCount = (rooms: ConfiguratorRoomDto[], roomId: number) => {
    const roomConfig = rooms.find((room) => room.roomId === roomId);
    if (!roomConfig) return 0;

    return roomConfig.groups.filter((group) => {
        const selectedOption = group.selectedOption ?? group.options.find((option) => option.selected) ?? null;
        return !selectedOption;
    }).length;
};

export interface OptionConfiguratorTabsProps {
    /**
     * Layout mode. 'auth' is the standard sticky bar with the Overview tab + Sign-and-send card.
     * 'showroom' and 'immersive' both render the compact, full-bleed bar (left action + Total).
     * 'immersive' is Back-only; 'showroom' shows host-configurable close / complete buttons driven
     * by URL query params (see useShowroomActions). Defaults to 'auth'.
     */
    mode?: 'auth' | 'showroom' | 'immersive';
    /** Immersive only: where the Back button navigates (the standard options page to return to). */
    backTo?: string;
}

export const OptionConfiguratorTabs: React.FC<OptionConfiguratorTabsProps> = ({ mode = 'auth', backTo }) => {
    const { selectedDeadlineId, configuratorData, projectData, rooms, baseUrl, tabsEnabled, setTabsHeight, viewOnly } =
        useOptionConfigurator();
    const tabsContainerRef = useRef<HTMLDivElement | null>(null);
    const history = useHistory();
    const isMobile = useBreakpoint().down('md');
    const isCompactChrome = useIsCompactChrome();
    const isBuyer = useIsBuyer();
    const stickyOffsetTop = isBuyer ? 72 : undefined;

    // 'showroom' and 'immersive' render the compact, full-bleed bar; 'auth' keeps the standard bar.
    const compact = mode !== 'auth';

    // Host-configurable close / complete actions for the anonymous showroom (via URL query params).
    // Only consulted in the showroom branch below.
    const showroom = useShowroomActions();

    // Signing lives in the standard options tree, not the immersive subtree, so from immersive we
    // target the exit base (drop the trailing `/configurator`); the standard `/deadline/:id/sign-pre`
    // route then renders the normal signing flow.
    const signBaseUrl = mode === 'immersive' ? baseUrl.replace(/\/configurator$/, '') : baseUrl;
    const handleSignClick = () => {
        if (!selectedDeadlineId) return;
        history.push(buildConfiguratorUrl({ baseUrl: signBaseUrl, deadlineId: selectedDeadlineId, segment: 'sign-pre' }));
    };

    // Immersive (authenticated) Back button: returns to the standard options page via `backTo`.
    // (The showroom's close action is host-configurable — see `showroom` / useShowroomActions.)
    const handleBack = () => {
        if (backTo) history.push(backTo);
    };

    useEffect(() => {
        if (!tabsEnabled || isMobile) {
            return;
        }

        const element = tabsContainerRef.current;
        if (!element) {
            return;
        }

        const updateHeight = () => {
            const rect = element.getBoundingClientRect();
            // Include the visual gap below the tabs row.
            setTabsHeight(Math.ceil(rect.height) + 16);
        };

        updateHeight();

        const observer = new ResizeObserver(updateHeight);
        observer.observe(element);
        window.addEventListener('resize', updateHeight);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateHeight);
        };
    }, [setTabsHeight, isMobile, tabsEnabled]);

    // useOnWindowResize(updateTabsHeight, 50);
    if (!tabsEnabled || !projectData) {
        return null;
    }

    const roomsWithOptions = rooms
        .filter((room) => room.roomId !== 0)
        .map((room) => ({
            id: room.roomId,
            label: room.roomName,
            icon: room.icon ? (['fal', room.icon] as IconProp) : faTag,
        }));
    const mainStyleGroups = configuratorData?.styleGroups.filter((x) => x.type === OptionPresetGroupType.Main) || [];
    const generalRoom = rooms.find((x) => x.roomId === 0);

    const getStyleGroupDisplayName = (styleGroup: ConfiguratorPresetGroupDto) => {
        const pickedStyle = styleGroup.styles?.find((style) => style.isPicked);
        return pickedStyle ? pickedStyle.name : styleGroup.name;
    };

    const selectedDeadline = projectData.deadlines.find((d) => d.id === selectedDeadlineId);

    // Disable sign and send if there's a force-selection style with no selection, or deadline expired.
    const hasUnpickedStyleGroups = !!configuratorData?.styleGroups.some((group) => {
        if (group.type === OptionPresetGroupType.Room && !group.roomId) return false;
        return group.forceSelection && !group.styles.some((s) => s.isPicked);
    });
    const isDeadlineExpired = selectedDeadline
        ? new Date(selectedDeadline.deadline) < new Date(new Date().toDateString())
        : false;

    // The deadline only blocks buyers. A seller/admin signs on behalf of the unit precisely when the
    // deadline has passed, so expiry must not disable their sign button (the backend allows it too).
    const disableSignAndSend = hasUnpickedStyleGroups || (isDeadlineExpired && isBuyer);

    // Compact modes (immersive/showroom) use the condensed room switcher below 960px and the desktop
    // room-tabs bar at/above it (the tabs need the width). Auth keeps the standard rubus breakpoint.
    if (compact ? isCompactChrome : isMobile) {
        return <OptionConfiguratorTabsMobile mode={mode} backTo={backTo} />;
    }

    // The compact bar (showroom + immersive) is a chromeless, full-bleed top row in the parent
    // column-flex layout. Sticky positioning would otherwise calculate an offset against the
    // (hidden) global header and end up at top: -71px.
    const TabsWrapper: React.ElementType = compact ? 'div' : Sticky;
    const wrapperProps = compact
        ? { className: classnames(theme.tabsContainer, theme.showroomTabs) }
        : { stickyClassName: theme.sticky, className: theme.tabsContainer, offsetTop: stickyOffsetTop };

    return (
        <TabsWrapper {...wrapperProps}>
            <div ref={tabsContainerRef} className={theme.tabsInner}>
                {mode === 'immersive' && (
                    <>
                        <div className={theme.showroomLeft}>
                            <Button variant="tertiary" size="sm" icon={faArrowLeft} onClick={handleBack}>
                                <Trans>Back</Trans>
                            </Button>
                        </div>
                        <div className={theme.divider} />
                    </>
                )}
                {/* Showroom close button: opt-in and host-configurable via the `close_label` param. */}
                {mode === 'showroom' && showroom.closeLabel && (
                    <>
                        <div className={theme.showroomLeft}>
                            <Button variant="tertiary" size="sm" icon={faArrowLeft} onClick={showroom.onClose}>
                                {showroom.closeLabel}
                            </Button>
                        </div>
                        <div className={theme.divider} />
                    </>
                )}
                {!compact && (
                    <Card className={theme.styleTabs}>
                        <Button
                            variant="dark-link"
                            as={NavLink}
                            exact
                            to={buildConfiguratorUrl({ baseUrl, deadlineId: selectedDeadlineId })}
                            icon={faLineColumns}
                            activeClassName={theme.active}
                            className={classnames(theme.tab)}>
                            <Trans>Overview</Trans>
                        </Button>
                    </Card>
                )}

                {/* Presets tab: auth only. Immersive + showroom select styles per room via the
                    group-list "Styles" section + modal (see RoomStyleSection), not top-level tabs. */}
                {!compact && mainStyleGroups.length > 0 && (
                    <Card className={theme.styleTabs}>
                        {mainStyleGroups.map((styleGroup) => (
                            <Button
                                as={NavLink}
                                key={`style-${styleGroup.id}`}
                                className={classnames(theme.tab)}
                                activeClassName={theme.active}
                                variant="dark-link"
                                to={buildConfiguratorUrl({
                                    baseUrl,
                                    deadlineId: selectedDeadlineId,
                                    segment: `style-group/${styleGroup.id}`,
                                })}
                                icon={faSwatchbook}>
                                <span className={theme.withDot}>
                                    <span className={theme.roomLabelText}>{getStyleGroupDisplayName(styleGroup)}</span>
                                    {styleGroup.forceSelection && !styleGroup.styles.some((s) => s.isPicked) && (
                                        <Dot variant="warning" />
                                    )}
                                </span>
                            </Button>
                        ))}
                    </Card>
                )}

                <Card className={theme.card}>
                    <div className={theme.tabs}>
                        {generalRoom && (
                            <Button
                                as={NavLink}
                                key={generalRoom.roomId}
                                className={classnames(theme.tab)}
                                activeClassName={theme.active}
                                variant="dark-link"
                                icon={faStar}
                                to={buildConfiguratorUrl({
                                    baseUrl,
                                    deadlineId: selectedDeadlineId,
                                    segment: `room/${generalRoom.roomId}`,
                                })}>
                                <span className={theme.withDot}>
                                    <span className={theme.roomLabelText}>
                                        <Trans>General options</Trans>
                                    </span>
                                    {getUnselectedGroupCount(rooms, generalRoom.roomId) > 0 && (
                                        <Dot variant="warning" />
                                    )}
                                </span>
                            </Button>
                        )}
                        {roomsWithOptions.map((room) => (
                            <Button
                                as={NavLink}
                                key={room.id}
                                className={classnames(theme.tab)}
                                activeClassName={theme.active}
                                variant="dark-link"
                                icon={room.icon}
                                to={buildConfiguratorUrl({
                                    baseUrl,
                                    deadlineId: selectedDeadlineId,
                                    segment: `room/${room.id}`,
                                })}>
                                <span className={theme.withDot}>
                                    <span className={theme.roomLabelText}>{room.label}</span>
                                    {getUnselectedGroupCount(rooms, room.id) > 0 && <Dot variant="warning" />}
                                </span>
                            </Button>
                        ))}
                    </div>

                    <div className={theme.rightSection}>
                        {compact && (
                            <div className={theme.showroomTotal}>
                                <span className={theme.showroomTotalLabel}>
                                    <Trans>Total</Trans>
                                </span>
                                <span className={theme.showroomTotalValue}>
                                    <BuyerCurrencyDisplay value={configuratorData?.grandPrice ?? 0} />
                                </span>
                            </div>
                        )}
                        {/* Showroom complete button: opt-in and host-configurable via the `complete_label`
                            param. Emits `showroom-complete` (or navigates to `complete_url`). */}
                        {mode === 'showroom' && showroom.completeLabel && (
                            <Button variant="primary" size="sm" onClick={showroom.onComplete}>
                                {showroom.completeLabel}
                            </Button>
                        )}
                        {/* Immersive (authenticated) buyer: sign-and-send next to the total, with a
                            pre-check that offers to apply standard selections for pending groups. */}
                        {mode === 'immersive' && (
                            <ImmersiveSignAndSend
                                disabled={disableSignAndSend}
                                viewOnly={viewOnly}
                                size="sm"
                                onProceed={handleSignClick}
                            />
                        )}
                        {/* {projectData.deadlines && projectData.deadlines.length > 1 && (
                        <Select
                            value={deadlineOptions.find((x) => x.value === selectedDeadlineId)}
                            onChange={(option) => {
                                if (option && 'value' in option) {
                                    setSelectedDeadlineId(option.value);
                                }
                            }}
                            options={deadlineOptions}
                        />
                    )} */}
                    </div>
                </Card>
                {!compact && (
                    <Card className={theme.signAndSendCard}>
                        <div className={theme.signAndSend}>
                            {viewOnly ? (
                                <SimpleTooltip
                                    placement="bottom"
                                    content={<Trans>Signing is disabled in this view</Trans>}>
                                    <Button disabled icon={faFileSignature}>
                                        <Trans>Sign and send</Trans>
                                    </Button>
                                </SimpleTooltip>
                            ) : (
                                <Button disabled={disableSignAndSend} onClick={handleSignClick} icon={faFileSignature}>
                                    <Trans>Sign and send</Trans>
                                </Button>
                            )}
                        </div>
                    </Card>
                )}
            </div>
        </TabsWrapper>
    );
};

export const OptionConfiguratorTabsMobile: React.FC<OptionConfiguratorTabsProps> = ({ mode = 'auth', backTo }) => {
    const isBuyer = useIsBuyer();
    const stickyOffsetTop = isBuyer ? 72 : undefined;
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const history = useHistory();
    // The compact bar (showroom + immersive) hides the auth-only overview/sign affordances.
    const compact = mode !== 'auth';
    // Back/close (bare arrow icon, left of the room switcher) and complete for the compact top bar.
    // This bar is shown below 960px for the compact modes; wider viewports use the desktop tabs bar.
    // The complete action joins the top bar at/above 768px; below that it stays in the bottom bar.
    const { close, complete } = useConfiguratorPrimaryActions(mode, backTo);
    const completeInTopBar = useCompleteInTopBar();

    const { selectedDeadlineId, configuratorData, projectData, rooms, baseUrl, tabsEnabled, viewOnly } = useOptionConfigurator();

    const mainStyleGroups = configuratorData?.styleGroups.filter((x) => x.type === OptionPresetGroupType.Main) || [];
    const generalRoom = rooms.find((x) => x.roomId === 0);

    if (!tabsEnabled || !projectData) {
        return null;
    }

    const roomsWithOptions = rooms
        .filter((room) => room.roomId !== 0)
        .map((room) => ({
            id: room.roomId,
            label: room.roomName,
            icon: room.icon ? (['fal', room.icon] as IconProp) : faTag,
        }));

    const getStyleGroupDisplayName = (styleGroup: ConfiguratorPresetGroupDto) => {
        const pickedStyle = styleGroup.styles?.find((style) => style.isPicked);
        return pickedStyle ? pickedStyle.name : styleGroup.name;
    };

    const styleGroupMatch = location.pathname.match(/\/style-group\/(\d+)/);
    const styleGroupId = styleGroupMatch ? styleGroupMatch[1] : undefined;

    const currentStyleGroup = styleGroupId
        ? configuratorData?.styleGroups.find((x) => x.id === Number(styleGroupId))
        : undefined;

    const roomMatch = location.pathname.match(/\/room\/(\d+)/);
    const currentRoomId = roomMatch ? Number(roomMatch[1]) : undefined;
    const isGeneralOptions = currentRoomId === 0;
    const currentRoom =
        currentRoomId !== undefined && currentRoomId !== 0
            ? roomsWithOptions.find((r) => r.id === currentRoomId)
            : undefined;

    const getSelectorLabel = () => {
        if (isGeneralOptions) return <Trans>General options</Trans>;
        if (currentRoom) return currentRoom.label;
        if (currentStyleGroup) return getStyleGroupDisplayName(currentStyleGroup);
        return <Trans>Select room</Trans>;
    };

    const getSelectorIcon = (): IconProp => {
        if (isGeneralOptions) return faStar;
        if (currentRoom) return currentRoom.icon;
        if (currentStyleGroup) return faSwatchbook;
        return faLoveseat;
    };

    // Immersive signing exits to the standard options tree (drop the trailing `/configurator`).
    const signBaseUrl = mode === 'immersive' ? baseUrl.replace(/\/configurator$/, '') : baseUrl;
    const handleSignClick = () => {
        if (!selectedDeadlineId) return;
        history.push(buildConfiguratorUrl({ baseUrl: signBaseUrl, deadlineId: selectedDeadlineId, segment: 'sign-pre' }));
    };

    const hasUnpickedForceSelection = configuratorData?.styleGroups.some((x) => {
        if (x.type === OptionPresetGroupType.Room && !x.roomId) return false;
        if (x.forceSelection && !x.styles.some((s) => s.isPicked)) return true;
        return false;
    });
    const hasUnpickedStyleGroups = !!configuratorData?.styleGroups.some((group) => {
        if (group.type === OptionPresetGroupType.Room && !group.roomId) return false;
        return group.forceSelection && !group.styles.some((s) => s.isPicked);
    });
    const selectedDeadline = projectData?.deadlines.find((d) => d.id === selectedDeadlineId);
    const isDeadlineExpired = selectedDeadline
        ? new Date(selectedDeadline.deadline) < new Date(new Date().toDateString())
        : false;
    // Expiry only blocks buyers; a seller/admin signs on behalf of the unit after the deadline (see desktop).
    const disableSignAndSend = hasUnpickedForceSelection || hasUnpickedStyleGroups || (isDeadlineExpired && isBuyer);

    const isOverviewPage =
        location.pathname === `${baseUrl}` || location.pathname === `${baseUrl}/deadline/${selectedDeadlineId}`;

    // "Selections due" indicator for the collapsed mobile room switcher: mirrors the per-room dots on
    // the desktop room tabs (and in the switch-rooms dialog). A dot on the room button signals that
    // some room still needs selections — tap to open the dialog and see which.
    const anySelectionDue = rooms.some((room) => getUnselectedGroupCount(rooms, room.roomId) > 0);

    // The compact (showroom + immersive) shells own their own flex layout: the bar is a static top
    // row above a separate scroll container, so it must stay in normal flow. Sticky would compute its
    // offset against the (hidden) global header, land at a negative top, and float out of flow —
    // overlaying the content below (e.g. the room visualization). Only auth (inside the global page +
    // header) needs the sticky bar. Mirrors the desktop branch above.
    const TabsWrapper: React.ElementType = compact ? 'div' : Sticky;
    const wrapperProps = compact
        ? { className: theme.mobileTabsContainer }
        : { stickyClassName: theme.sticky, className: theme.mobileTabsContainer, offsetTop: stickyOffsetTop };

    return (
        <TabsWrapper {...wrapperProps}>
            {compact ? (
                // Compact (immersive/showroom): a bare back arrow + condensed room switcher on the
                // left, the running total on the right — plus the complete action at/above 768px
                // (below that, complete stays in the bottom action bar).
                <div className={theme.compactBar}>
                    <div className={theme.compactLeft}>
                        {close && (
                            // Bare back arrow (no button chrome), left of the room switcher.
                            <button
                                type="button"
                                className={theme.compactBack}
                                aria-label={close.ariaLabel}
                                onClick={close.onClick}>
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </button>
                        )}
                        <button type="button" className={theme.roomButton} onClick={() => setOpen(true)}>
                            <FontAwesomeIcon icon={getSelectorIcon()} className={theme.roomButtonIcon} />
                            <span className={theme.roomButtonName}>{getSelectorLabel()}</span>
                            {anySelectionDue && <Dot variant="warning" />}
                            <FontAwesomeIcon icon={faRightLeft} className={theme.roomButtonSwitch} />
                        </button>
                    </div>
                    <div className={theme.compactRight}>
                        <div className={theme.compactTotal}>
                            <span className={theme.compactTotalLabel}>
                                <Trans>Total</Trans>
                            </span>
                            <span className={theme.compactTotalValue}>
                                <BuyerCurrencyDisplay value={configuratorData?.grandPrice ?? 0} />
                            </span>
                        </div>
                        {completeInTopBar && complete && <PrimaryCompleteControl complete={complete} size="sm" />}
                    </div>
                </div>
            ) : (
                <>
                    {!isOverviewPage && (
                        <div className={theme.closeAndDeadlineRow}>
                            <Button
                                variant="tertiary"
                                icon={faLineColumns}
                                onClick={() => {
                                    history.push(buildConfiguratorUrl({ baseUrl, deadlineId: selectedDeadlineId }));
                                    setOpen(false);
                                }}>
                                <Trans>Back to overview</Trans>
                            </Button>
                        </div>
                    )}

                    <div className={theme.stylesAndSignRow}>
                        {/* Standard buyer config: direct sign-and-send. */}
                        {viewOnly ? (
                            <SimpleTooltip placement="bottom" content={<Trans>Signing is disabled in this view</Trans>}>
                                <Button disabled icon={faFileSignature}>
                                    <Trans>Sign and send</Trans>
                                </Button>
                            </SimpleTooltip>
                        ) : (
                            <Button disabled={disableSignAndSend} onClick={handleSignClick} icon={faFileSignature}>
                                <Trans>Sign and send</Trans>
                            </Button>
                        )}
                        <Button variant="tertiary" onClick={() => setOpen(true)} icon={getSelectorIcon()}>
                            {getSelectorLabel()}
                        </Button>
                    </div>
                </>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <Dialog.Content className={theme.roomDialog}>
                    <Dialog.Header>
                        <Trans>Switch rooms</Trans>
                    </Dialog.Header>
                    <div className={theme.roomDialogContent}>
                    {/* Presets section: auth only (immersive + showroom use the per-room Styles section). */}
                    {!compact && mainStyleGroups.length > 0 && (
                        <div className={theme.section}>
                            <h3 className={theme.sectionTitle}>
                                <Trans>Styles</Trans>
                            </h3>
                            <div className={theme.styleGroupList}>
                                {mainStyleGroups.map((styleGroup) => (
                                    <NavLink
                                        onClick={() => setOpen(false)}
                                        key={`style-${styleGroup.id}`}
                                        className={theme.tab}
                                        to={buildConfiguratorUrl({
                                            baseUrl,
                                            deadlineId: selectedDeadlineId,
                                            segment: `style-group/${styleGroup.id}`,
                                        })}>
                                        <FontAwesomeIcon icon={faSwatchbook} className={theme.tabIcon} />
                                        <span className={theme.withDot}>
                                            {getStyleGroupDisplayName(styleGroup)}
                                            {styleGroup.forceSelection && !styleGroup.styles.some((s) => s.isPicked) && (
                                                <Dot variant="warning" />
                                            )}
                                        </span>
                                        <FontAwesomeIcon icon={faChevronRight} className={theme.tabChevron} />
                                    </NavLink>
                                ))}
                            </div>
                        </div>
                    )}

                    {(roomsWithOptions.length > 0 || !!generalRoom) && (
                        <div className={theme.section}>
                            {generalRoom && (
                                <NavLink
                                    onClick={() => setOpen(false)}
                                    key={generalRoom.roomId}
                                    className={theme.tab}
                                    to={buildConfiguratorUrl({
                                        baseUrl,
                                        deadlineId: selectedDeadlineId,
                                        segment: `room/${generalRoom.roomId}`,
                                    })}>
                                    <FontAwesomeIcon icon={faStar} className={theme.tabIcon} />
                                    <span className={theme.withDot}>
                                        <Trans>General options</Trans>
                                        {getUnselectedGroupCount(rooms, generalRoom.roomId) > 0 && (
                                            <Dot variant="warning" />
                                        )}
                                    </span>
                                    <FontAwesomeIcon icon={faChevronRight} className={theme.tabChevron} />
                                </NavLink>
                            )}
                            {roomsWithOptions.map((room) => (
                                <NavLink
                                    onClick={() => setOpen(false)}
                                    key={room.id}
                                    className={theme.tab}
                                    to={buildConfiguratorUrl({
                                        baseUrl,
                                        deadlineId: selectedDeadlineId,
                                        segment: `room/${room.id}`,
                                    })}>
                                    <FontAwesomeIcon icon={room.icon} className={theme.tabIcon} />
                                    <span className={theme.withDot}>
                                        {room.label}
                                        {getUnselectedGroupCount(rooms, room.id) > 0 && <Dot variant="warning" />}
                                    </span>
                                    <FontAwesomeIcon icon={faChevronRight} className={theme.tabChevron} />
                                </NavLink>
                            ))}
                        </div>
                    )}
                    {!compact && !isOverviewPage && (
                        <div className={theme.backToOverview}>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    history.push(buildConfiguratorUrl({ baseUrl, deadlineId: selectedDeadlineId }));
                                    setOpen(false);
                                }}>
                                <Trans>Back to overview</Trans>
                            </Button>
                        </div>
                    )}
                        {/* Explicit dismiss so the user can stay in the current room. */}
                        <div className={theme.roomDialogFooter}>
                            <Button variant="tertiary" onClick={() => setOpen(false)}>
                                <Trans>Close</Trans>
                            </Button>
                        </div>
                    </div>
                </Dialog.Content>
            </Dialog>
        </TabsWrapper>
    );
};
